// The THINKING ORB — the calm motion affordance shown while a run is in flight but has produced no
// structured event yet. Three dots counter-orbit a breathing core: the outer rings turn at different
// speeds and directions, and each dot pulses on its own stagger, so the mark reads as *deliberation*
// rather than the single blinking dot it replaces.
//
// Presentation only. It renders no run state, fabricates no turn (P-SELF) and carries no copy — the
// caller owns the label. Geometry and colour live in chat-thread.css against governed tokens (G-181);
// this file owns only the structure and the reduced-motion decision.

/** True when the client should render motion STATIC — the human asked for reduced motion. The check is
 *  behavioural (matchMedia) so the affordance survives while the motion stops (design brief §3 calm
 *  motion). Guarded for environments without matchMedia (defaults to motion-on). */
export function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/** The orbiting thinking mark. Decorative: the caller's row carries `role="status"` and the words, so
 *  the orb itself is hidden from assistive tech. Under reduced motion every ring and dot holds still
 *  (`data-static`), leaving a composed, legible mark instead of a frozen frame. */
export function ThinkingOrb(): React.JSX.Element {
  const reduce = prefersReducedMotion();
  return (
    <span
      aria-hidden="true"
      data-testid="thinking-orb"
      className="ritona-orb"
      data-static={reduce ? 'true' : undefined}
    >
      <span className="orb-glow" />
      <span className="orb-ring r1">
        <span className="orb-dot" />
      </span>
      <span className="orb-ring r2">
        <span className="orb-dot" />
      </span>
      <span className="orb-ring r3">
        <span className="orb-dot" />
      </span>
    </span>
  );
}
